<script setup lang="ts">
    import { onMounted, reactive, computed } from 'vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import {
        getVHosts,
        getErrorQueues,
        peekMessages,
        retryByType,
        groupByType,
        ErrorQueueSummary,
        RabbitMessage,
        MessageTypeGroup,
    } from './MessageManager.access';
    import Loading from '@shared/components/Loading.vue';
    import FluidSelect from '@shared/components/FluidSelect.vue';

    const state = reactive<{
        loading: boolean;
        vhosts: string[];
        selectedVhost: string;
        queues: ErrorQueueSummary[];
        selectedQueue: ErrorQueueSummary | null;
        peeking: boolean;
        messages: RabbitMessage[];
        peekCount: number;
        sampleCount: number;
        retryCount: number;
        retrying: string | null;
        looping: string | null;
        loopDelay: number;
        loopTotal: number;
        targetQueues: Record<string, string>;
        expandedMessage: RabbitMessage | null;
    }>({
        loading: false,
        vhosts: [],
        selectedVhost: '',
        queues: [],
        selectedQueue: null,
        peeking: false,
        messages: [],
        peekCount: 200,
        sampleCount: 1000,
        retryCount: 100,
        retrying: null,
        looping: null,
        loopDelay: 3,
        loopTotal: 0,
        targetQueues: {},
        expandedMessage: null,
    });


    const typeGroups = computed<MessageTypeGroup[]>(() => groupByType(state.messages));

    onMounted(async () => {
        state.loading = true;
        try {
            state.vhosts = await getVHosts();
            if (state.vhosts.length > 0) {
                state.selectedVhost = state.vhosts.find(v => v === 'prod') ?? state.vhosts[0];
                await loadQueues();
            }
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to connect to RabbitMQ management API' });
        } finally {
            state.loading = false;
        }
    });

    async function loadQueues(): Promise<void> {
        state.queues = await getErrorQueues(state.selectedVhost);
        state.selectedQueue = null;
        state.messages = [];
    }

    async function openQueue(queue: ErrorQueueSummary): Promise<void> {
        state.selectedQueue = queue;
        state.messages = [];
        state.targetQueues = {};
        state.expandedMessage = null;
        await peek();
    }

    function defaultTargetQueue(queue: ErrorQueueSummary): string {
        return queue.Name === 'error' ? '' : queue.Name.replace(/\.Error$/, '');
    }

    function targetQueueFor(group: MessageTypeGroup): string {
        const key = group.messageType ?? '(unknown)';
        if (!(key in state.targetQueues)) {
            state.targetQueues[key] = state.selectedQueue ? defaultTargetQueue(state.selectedQueue) : '';
        }
        return state.targetQueues[key];
    }

    async function peek(): Promise<void> {
        if (!state.selectedQueue) return;
        state.peeking = true;
        try {
            state.messages = await peekMessages(state.selectedVhost, state.selectedQueue.Name, state.peekCount);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to peek messages' });
        } finally {
            state.peeking = false;
        }
    }

    async function retry(group: MessageTypeGroup): Promise<void> {
        if (!state.selectedQueue || !group.messageType) return;
        const target = targetQueueFor(group);
        if (!target) {
            bsAlert({ type: AlertTypes.Warning, title: 'Missing target', content: 'Specify a target queue before retrying' });
            return;
        }
        state.retrying = group.messageType;
        try {
            const result = await retryByType(state.selectedVhost, state.selectedQueue.Name, group.messageType, state.retryCount, target);
            bsAlert({ type: AlertTypes.Success, title: 'Done', content: `Retried ${result.retried} messages to ${target}` });
            await reloadQueue();
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Retry failed' });
        } finally {
            state.retrying = null;
        }
    }

    async function startLoop(group: MessageTypeGroup): Promise<void> {
        if (!state.selectedQueue || !group.messageType) return;
        const target = targetQueueFor(group);
        if (!target) {
            bsAlert({ type: AlertTypes.Warning, title: 'Missing target', content: 'Specify a target queue before retrying' });
            return;
        }
        state.looping = group.messageType;
        state.loopTotal = 0;
        try {
            while (state.looping === group.messageType) {
                const result = await retryByType(state.selectedVhost, state.selectedQueue!.Name, group.messageType, state.retryCount, target);
                state.loopTotal += result.retried;
                if (result.retried === 0) {
                    bsAlert({ type: AlertTypes.Success, title: 'Loop done', content: `No more matching messages. Total retried: ${state.loopTotal}` });
                    break;
                }
                await reloadQueue();
                await new Promise(resolve => setTimeout(resolve, state.loopDelay * 1000));
            }
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Loop failed' });
        } finally {
            state.looping = null;
        }
    }

    function stopLoop(): void {
        state.looping = null;
    }

    async function sample(): Promise<void> {
        if (!state.selectedQueue) return;
        state.peeking = true;
        try {
            state.messages = await peekMessages(state.selectedVhost, state.selectedQueue.Name, state.sampleCount);
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: 'Error', content: 'Failed to load sample' });
        } finally {
            state.peeking = false;
        }
    }

    async function reloadQueue(): Promise<void> {
        if (!state.selectedQueue) return;
        state.queues = await getErrorQueues(state.selectedVhost);
        const updated = state.queues.find(q => q.Name === state.selectedQueue!.Name);
        state.selectedQueue = updated ?? null;
        if (state.selectedQueue) await peek();
    }
</script>

<template>
    <Loading :show="state.loading" />

    <h3>Error Queues</h3>

    <div v-if="!state.loading">
        <div class="form-inline m-sm-b">
            <label class="m-sm-r">VHost</label>
            <select v-model="state.selectedVhost" class="form-control" style="width:auto" @change="loadQueues">
                <option v-for="v in state.vhosts" :key="v" :value="v">{{ v }}</option>
            </select>
        </div>

        <div class="row">
            <!-- Queue list -->
            <div class="col-md-4">
                <table class="table table-hover table-condensed">
                    <thead>
                        <tr>
                            <th>Queue</th>
                            <th class="text-right">Messages</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="state.queues.length === 0">
                            <td colspan="2" class="text-muted">No error queues found</td>
                        </tr>
                        <tr
                            v-for="q in state.queues"
                            :key="q.Name"
                            :class="{ active: state.selectedQueue?.Name === q.Name }"
                            style="cursor:pointer"
                            @click="openQueue(q)">
                            <td>{{ q.Name }}</td>
                            <td class="text-right">
                                <span :class="q.Messages > 0 ? 'label label-danger' : 'label label-default'">
                                    {{ q.Messages.toLocaleString() }}
                                </span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Queue detail -->
            <div v-if="state.selectedQueue" class="col-md-8">
                <div class="panel panel-default">
                    <div class="panel-heading">
                        <h4 class="panel-title" style="display:flex;justify-content:space-between;align-items:center">
                            <span>{{ state.selectedQueue.Name }}</span>
                            <span style="display:flex;align-items:center;gap:8px">
                                <button class="btn btn-xs btn-default" :disabled="state.peeking" @click="peek">Refresh ({{ state.peekCount }})</button>
                                <input v-model.number="state.sampleCount" type="number" min="100" max="5000" step="100" class="form-control input-xs" style="width:70px" title="Sample size" />
                                <button class="btn btn-xs btn-primary" :disabled="state.peeking" @click="sample">Sample all types</button>
                            </span>
                        </h4>
                    </div>
                    <div class="panel-body">
                        <Loading :show="state.peeking" />

                        <div v-if="!state.peeking && state.messages.length === 0" class="text-muted">
                            No messages (queue may be empty or count is 0)
                        </div>

                        <template v-if="state.messages.length > 0">
                            <div class="form-inline m-sm-b" style="display:flex;align-items:center;gap:12px">
                                <label>Peek count</label>
                                <input v-model.number="state.peekCount" type="number" min="1" max="5000" class="form-control" style="width:80px" />
                                <label>Retry count</label>
                                <input v-model.number="state.retryCount" type="number" min="1" max="5000" class="form-control" style="width:80px" />
                                <label>Loop delay (s)</label>
                                <input v-model.number="state.loopDelay" type="number" min="1" max="60" class="form-control" style="width:60px" />
                                <small class="text-muted">Showing {{ state.messages.length }} of {{ state.selectedQueue.Messages.toLocaleString() }}</small>
                            </div>

                            <!-- Type breakdown -->
                            <table class="table table-condensed">
                                <thead>
                                    <tr>
                                        <th>Message type</th>
                                        <th class="text-right">In sample</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="group in typeGroups" :key="group.messageType">
                                        <tr>
                                            <td>
                                                <span :title="group.messageType ?? ''">{{ group.shortType }}</span>
                                            </td>
                                            <td class="text-right">{{ group.count }}</td>
                                            <td style="display:flex;align-items:center;gap:6px">
                                                <input
                                                    :value="targetQueueFor(group)"
                                                    @input="state.targetQueues[group.messageType ?? '(unknown)'] = ($event.target as HTMLInputElement).value"
                                                    placeholder="Target queue"
                                                    class="form-control input-xs"
                                                    style="width:140px" />
                                                <button
                                                    class="btn btn-xs btn-warning"
                                                    :disabled="state.retrying !== null || state.looping !== null"
                                                    @click="retry(group)">
                                                    <span v-if="state.retrying === group.messageType">Retrying…</span>
                                                    <span v-else>Retry {{ state.retryCount }}</span>
                                                </button>
                                                <template v-if="state.looping === group.messageType">
                                                    <span class="text-muted" style="font-size:11px">{{ state.loopTotal }} retried</span>
                                                    <button class="btn btn-xs btn-danger" @click="stopLoop">Stop</button>
                                                </template>
                                                <button
                                                    v-else
                                                    class="btn btn-xs btn-default"
                                                    :disabled="state.retrying !== null || state.looping !== null"
                                                    @click="startLoop(group)">
                                                    Loop
                                                </button>
                                            </td>
                                        </tr>
                                        <!-- Expandable messages for this type -->
                                        <tr v-for="msg in group.messages" :key="msg.MessageId" style="background:#fafafa">
                                            <td colspan="2" style="padding-left:24px;font-size:12px;color:#888">
                                                {{ msg.ErrorSummary || '(no error summary)' }}
                                            </td>
                                            <td>
                                                <button
                                                    class="btn btn-xs btn-link"
                                                    @click="state.expandedMessage = state.expandedMessage === msg ? null : msg">
                                                    {{ state.expandedMessage === msg ? 'Close' : 'Inspect' }}
                                                </button>
                                            </td>
                                        </tr>
                                        <tr v-if="state.expandedMessage && group.messages.includes(state.expandedMessage)" :key="state.expandedMessage.MessageId + '-detail'">
                                            <td colspan="3" style="background:#f5f5f5;padding:12px">
                                                <div class="m-sm-b">
                                                    <strong>Source queue:</strong> {{ state.expandedMessage.SourceQueue }}<br />
                                                    <strong>Type:</strong> {{ state.expandedMessage.MessageType }}
                                                </div>
                                                <div class="m-sm-b">
                                                    <strong>Body</strong>
                                                    <textarea class="form-control" rows="6" readonly :value="state.expandedMessage.Body" style="font-family:monospace;font-size:11px"></textarea>
                                                </div>
                                                <div>
                                                    <strong>Headers</strong>
                                                    <table class="table table-condensed" style="font-size:11px">
                                                        <tbody>
                                                            <tr v-for="(value, key) in state.expandedMessage.Headers" :key="key">
                                                                <td style="width:220px;word-break:break-all">{{ key }}</td>
                                                                <td style="word-break:break-all">{{ value }}</td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    export default {
        name: 'MessageManager',
    };
</script>